import React, { useState, useMemo, useRef, useCallback, forwardRef } from 'react';
import ReactQuill, { Quill } from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import './RichTextEditor.css';
import { Box, Paper, Typography, IconButton, Tooltip } from '@mui/material';
import { styled } from '@mui/material/styles';
import EmojiEmotionsIcon from '@mui/icons-material/EmojiEmotions';
import InfoIcon from '@mui/icons-material/Info';
import FormatSizeIcon from '@mui/icons-material/FormatSize';
import ImageIcon from '@mui/icons-material/Image';
import IconPicker from './IconPicker';
import CalloutPicker from './CalloutPicker';
import FontStylePicker from './FontStylePicker';
import ImageUrlPicker from './ImageUrlPicker';

// Создаем обертку для ReactQuill с forwardRef для устранения предупреждения findDOMNode
const QuillWrapper = forwardRef((props, ref) => {
  return <ReactQuill ref={ref} {...props} />;
});

// Добавляем отображаемое имя для компонента
QuillWrapper.displayName = 'QuillWrapper';

// Стилизованный контейнер для редактора
const StyledEditorContainer = styled(Paper)(({ theme }) => ({
  '& .ql-toolbar': {
    borderTop: 'none',
    borderLeft: 'none',
    borderRight: 'none',
    borderBottom: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.default,
  },
  '& .ql-container': {
    borderBottom: 'none',
    borderLeft: 'none',
    borderRight: 'none',
    fontFamily: theme.typography.fontFamily,
    fontSize: '16px',
    lineHeight: '1.6',
  },
  '& .ql-editor': {
    minHeight: '300px',
    padding: theme.spacing(2),
    '& h1': {
      fontSize: '2.5rem',
      fontWeight: 700,
      marginBottom: theme.spacing(2),
      color: theme.palette.primary.main,
    },
    '& h2': {
      fontSize: '2rem',
      fontWeight: 600,
      marginBottom: theme.spacing(1.5),
      color: theme.palette.primary.main,
    },
    '& h3': {
      fontSize: '1.5rem',
      fontWeight: 600,
      marginBottom: theme.spacing(1),
      color: theme.palette.primary.main,
    },
    '& p': {
      marginBottom: theme.spacing(1.5),
      lineHeight: '1.7',
    },
    '& blockquote': {
      borderLeft: `4px solid ${theme.palette.primary.main}`,
      paddingLeft: theme.spacing(2),
      margin: theme.spacing(2, 0),
      fontStyle: 'italic',
      backgroundColor: theme.palette.action.hover,
      padding: theme.spacing(1, 2),
      borderRadius: theme.shape.borderRadius,
    },
    '& ul, & ol': {
      paddingLeft: theme.spacing(3),
      marginBottom: theme.spacing(2),
    },
    '& li': {
      marginBottom: theme.spacing(0.5),
    },
    '& img': {
      maxWidth: '100%',
      height: 'auto',
      borderRadius: theme.shape.borderRadius,
      boxShadow: theme.shadows[2],
      margin: theme.spacing(1, 0),
    },
    '& pre': {
      backgroundColor: theme.palette.grey[100],
      padding: theme.spacing(2),
      borderRadius: theme.shape.borderRadius,
      overflow: 'auto',
      fontSize: '14px',
      border: `1px solid ${theme.palette.divider}`,
    },
    '& code': {
      backgroundColor: theme.palette.grey[100],
      padding: theme.spacing(0.25, 0.5),
      borderRadius: '4px',
      fontSize: '0.9em',
    },
    '& a': {
      color: theme.palette.primary.main,
      textDecoration: 'none',
      '&:hover': {
        textDecoration: 'underline',
      },
    },
  },
}));

const RichTextEditor = ({ value, onChange, placeholder = "Начните писать вашу статью..." }) => {
  const quillRef = useRef(null);
  const [iconPickerOpen, setIconPickerOpen] = useState(false);
  const [calloutPickerOpen, setCalloutPickerOpen] = useState(false);
  const [fontStylePickerOpen, setFontStylePickerOpen] = useState(false);
  const [imageUrlPickerOpen, setImageUrlPickerOpen] = useState(false);
  const [savedCursorPosition, setSavedCursorPosition] = useState(null);

  // Функция для вставки иконки в редактор
  const insertIcon = (icon) => {
    if (quillRef.current) {
      const quill = quillRef.current.getEditor();
      // Используем сохраненную позицию курсора или текущую
      const position = savedCursorPosition !== null ? savedCursorPosition : (
        quill.getSelection() ? quill.getSelection().index : quill.getLength()
      );
      
      // Вставляем иконку как эмодзи или HTML
      quill.insertText(position, ` ${icon.code} `, 'user');
      quill.setSelection(position + icon.code.length + 2);
      
      // Очищаем сохраненную позицию
      setSavedCursorPosition(null);
    }
  };

  // Функция для вставки информационного блока
  const insertCallout = (calloutHtml) => {
    if (quillRef.current) {
      const quill = quillRef.current.getEditor();
      // Используем сохраненную позицию курсора или текущую
      const position = savedCursorPosition !== null ? savedCursorPosition : (
        quill.getSelection() ? quill.getSelection().index : quill.getLength()
      );
      
      // Вставляем HTML блок
      quill.clipboard.dangerouslyPasteHTML(position, calloutHtml);
      quill.setSelection(position + calloutHtml.length);
      
      // Очищаем сохраненную позицию
      setSavedCursorPosition(null);
    }
  };

  // Функция для вставки стилизованного текста
  const insertStyledText = (styledHtml) => {
    if (quillRef.current) {
      const quill = quillRef.current.getEditor();
      // Используем сохраненную позицию курсора или текущую
      const position = savedCursorPosition !== null ? savedCursorPosition : (
        quill.getSelection() ? quill.getSelection().index : quill.getLength()
      );
      
      // Вставляем стилизованный текст
      quill.clipboard.dangerouslyPasteHTML(position, styledHtml);
      quill.setSelection(position + styledHtml.length);
      
      // Очищаем сохраненную позицию
      setSavedCursorPosition(null);
    }
  };

  // Функция для вставки изображения
  const insertImage = (imageHtml) => {
    if (quillRef.current) {
      const quill = quillRef.current.getEditor();
      // Используем сохраненную позицию курсора или текущую
      const position = savedCursorPosition !== null ? savedCursorPosition : (
        quill.getSelection() ? quill.getSelection().index : quill.getLength()
      );
      
      // Вставляем изображение
      quill.clipboard.dangerouslyPasteHTML(position, imageHtml);
      quill.setSelection(position + imageHtml.length);
      
      // Очищаем сохраненную позицию
      setSavedCursorPosition(null);
    }
  };

  // Кастомный обработчик для вставки изображений в позицию курсора
  const imageHandler = useCallback(async () => {
    if (quillRef.current) {
      const quill = quillRef.current.getEditor();
      const range = quill.getSelection();
      // Сохраняем позицию курсора
      setSavedCursorPosition(range ? range.index : quill.getLength());
    }
    
    const input = document.createElement('input');
    input.setAttribute('type', 'file');
    input.setAttribute('accept', 'image/*');
    input.click();

    input.onchange = async () => {
      const file = input.files[0];
      if (file && quillRef.current) {
        try {
          // Загружаем файл на сервер
          const formData = new FormData();
          formData.append('image', file);
          
          // Определяем эндпоинт в зависимости от текущего URL
          const isEditingTip = window.location.pathname.includes('/tips/');
          const endpoint = isEditingTip ? '/api/tips/temp-images' : '/api/blog/temp-images';
          
          const response = await fetch(endpoint, {
            method: 'POST',
            body: formData,
            credentials: 'include'
          });
          
          if (response.ok) {
            const data = await response.json();
            const imageUrl = data.url || data.imageUrl;
            
            // Вставляем изображение в редактор
            const quill = quillRef.current.getEditor();
            const position = savedCursorPosition !== null ? savedCursorPosition : quill.getLength();
            quill.insertEmbed(position, 'image', imageUrl, 'user');
            quill.setSelection(position + 1);
            
            // Очищаем сохраненную позицию
            setSavedCursorPosition(null);
          } else {
            console.error('Ошибка загрузки изображения:', response.statusText);
            // Если загрузка не удалась, показываем URL picker
            setImageUrlPickerOpen(true);
          }
        } catch (error) {
          console.error('Ошибка загрузки изображения:', error);
          // Если загрузка не удалась, показываем URL picker
          setImageUrlPickerOpen(true);
        }
      }
    };
  }, [savedCursorPosition]);

  // Кастомный обработчик для вставки видео в позицию курсора
  const videoHandler = useCallback(() => {
    const url = prompt('Введите URL видео:');
    if (url && quillRef.current) {
      const quill = quillRef.current.getEditor();
      const range = quill.getSelection();
      const position = range ? range.index : quill.getLength();
      
      // Вставляем видео в позицию курсора
      quill.insertEmbed(position, 'video', url, 'user');
      quill.setSelection(position + 1);
    }
  }, []);

  // Функция для открытия диалога вставки изображения по URL
  const openImageUrlPicker = useCallback(() => {
    if (quillRef.current) {
      const quill = quillRef.current.getEditor();
      const range = quill.getSelection();
      // Сохраняем позицию курсора
      setSavedCursorPosition(range ? range.index : quill.getLength());
    }
    setImageUrlPickerOpen(true);
  }, []);

  // Функция для открытия диалога выбора иконок
  const openIconPicker = useCallback(() => {
    if (quillRef.current) {
      const quill = quillRef.current.getEditor();
      const range = quill.getSelection();
      setSavedCursorPosition(range ? range.index : quill.getLength());
    }
    setIconPickerOpen(true);
  }, []);

  // Функция для открытия диалога информационных блоков
  const openCalloutPicker = useCallback(() => {
    if (quillRef.current) {
      const quill = quillRef.current.getEditor();
      const range = quill.getSelection();
      setSavedCursorPosition(range ? range.index : quill.getLength());
    }
    setCalloutPickerOpen(true);
  }, []);

  // Функция для открытия диалога стилей шрифта
  const openFontStylePicker = useCallback(() => {
    if (quillRef.current) {
      const quill = quillRef.current.getEditor();
      const range = quill.getSelection();
      setSavedCursorPosition(range ? range.index : quill.getLength());
    }
    setFontStylePickerOpen(true);
  }, []);

  // Конфигурация модулей для редактора
  const modules = useMemo(() => ({
    toolbar: {
      container: [
        [{ 'header': [1, 2, 3, false] }],
        [{ 'font': [] }, { 'size': ['small', false, 'large', 'huge'] }],
        ['bold', 'italic', 'underline', 'strike'],
        [{ 'color': [] }, { 'background': [] }],
        [{ 'script': 'sub' }, { 'script': 'super' }],
        [{ 'list': 'ordered' }, { 'list': 'bullet' }],
        [{ 'indent': '-1' }, { 'indent': '+1' }],
        [{ 'align': [] }],
        ['blockquote', 'code-block'],
        ['link', 'image', 'video'],
        ['clean']
      ],
      handlers: {
        'image': imageHandler,
        'video': videoHandler
      }
    },
    clipboard: {
      matchVisual: false,
    },
  }), [imageHandler, videoHandler]);

  // Форматы, которые поддерживает редактор
  const formats = [
    'header', 'font', 'size',
    'bold', 'italic', 'underline', 'strike',
    'color', 'background',
    'script',
    'list', 'bullet', 'indent',
    'align',
    'blockquote', 'code-block',
    'link', 'image', 'video'
  ];

  return (
    <Box sx={{ mb: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography variant="h6">
          Содержание статьи
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Вставить иконку">
            <IconButton 
              onClick={openIconPicker}
              size="small"
              color="primary"
            >
              <EmojiEmotionsIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Добавить информационный блок">
            <IconButton 
              onClick={openCalloutPicker}
              size="small"
              color="primary"
            >
              <InfoIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Настройки шрифта">
            <IconButton 
              onClick={openFontStylePicker}
              size="small"
              color="primary"
            >
              <FormatSizeIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Вставить изображение">
            <IconButton 
              onClick={openImageUrlPicker}
              size="small"
              color="primary"
            >
              <ImageIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
      <StyledEditorContainer variant="outlined">
        <QuillWrapper
          ref={quillRef}
          theme="snow"
          value={value}
          onChange={onChange}
          modules={modules}
          formats={formats}
          placeholder={placeholder}
        />
      </StyledEditorContainer>
      
      <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
        💡 Используйте кнопки выше для добавления: 😊 иконок, ℹ️ информационных блоков, 🎨 стилей шрифта и 🖼️ изображений
      </Typography>
      
      <IconPicker
        open={iconPickerOpen}
        onClose={() => setIconPickerOpen(false)}
        onIconSelect={insertIcon}
      />
      
      <CalloutPicker
        open={calloutPickerOpen}
        onClose={() => setCalloutPickerOpen(false)}
        onCalloutSelect={insertCallout}
      />
      
      <FontStylePicker
        open={fontStylePickerOpen}
        onClose={() => setFontStylePickerOpen(false)}
        onStyleSelect={insertStyledText}
      />
      
      <ImageUrlPicker
        open={imageUrlPickerOpen}
        onClose={() => setImageUrlPickerOpen(false)}
        onImageSelect={insertImage}
      />
    </Box>
  );
};

export default RichTextEditor;
